import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { Dd } from '../components/Dropdown.js';
import { DateField } from '../components/DateTimeField.js';

/**
 * ehr#491: the bell's "Notification log" opened a placeholder ("on the v2.0 roadmap"). It is the
 * practice's log of Forms Hub notifications (GET /api/v1/notifications, newest first, 200 at most):
 * a form assigned, submitted, overdue, escalated, signed, or a reminder sent. Type and date filters.
 */
interface NotificationRow {
  id: string;
  type: string;
  ts: string;
  channel: string | null;
  detail: string | null;
  patient: { firstName: string | null; lastName: string | null } | null;
}

export const NOTIFICATION_TYPES: Record<string, string> = {
  assigned: 'Form assigned',
  submitted: 'Form submitted',
  overdue: 'Form overdue',
  escalated: 'Sign-off escalated',
  signed: 'Form signed',
  reminder: 'Reminder sent',
};

const when = (ts: string) =>
  new Date(ts).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
const clientOf = (r: NotificationRow) =>
  [r.patient?.firstName, r.patient?.lastName].filter(Boolean).join(' ').trim() || '—';
/** YYYY-MM-DD → the start (or end) of that local day as an ISO timestamp; '' when not a date. */
const dayBound = (iso: string, end: boolean): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return '';
  const d = end
    ? new Date(+m[1]!, +m[2]! - 1, +m[3]!, 23, 59, 59, 999)
    : new Date(+m[1]!, +m[2]! - 1, +m[3]!);
  return d.toISOString();
};

export function NotificationLogView() {
  const [type, setType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rows, setRows] = useState<NotificationRow[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    const qs = new URLSearchParams();
    if (type) qs.set('type', type);
    const f = dayBound(from, false);
    const t = dayBound(to, true);
    if (f) qs.set('from', f);
    if (t) qs.set('to', t);
    api
      .get<NotificationRow[]>(`/notifications${qs.toString() ? `?${qs}` : ''}`)
      .then((r) => {
        if (!live) return;
        setRows(r ?? []);
        setError('');
      })
      .catch(() => live && setError('Could not load the notification log.'));
    return () => {
      live = false;
    };
  }, [type, from, to]);

  const cols = '170px 160px 1fr 2fr 110px';
  return (
    <div className="admin-view">
      <div className="admin-head">
        <h1 className="admin-title">Notification Log</h1>
        <p className="muted">
          Forms Hub notifications for the practice, newest first: forms assigned, submitted, overdue
          and signed, sign-offs escalated, and reminders sent.
        </p>
      </div>
      <div className="card panel">
        <div className="filter-bar">
          <Dd
            value={type}
            onChange={setType}
            searchable={false}
            ariaLabel="Filter by type"
            options={[
              { value: '', label: 'All types' },
              ...Object.entries(NOTIFICATION_TYPES).map(([value, label]) => ({ value, label })),
            ]}
          />
          <div className="audit-dates">
            <DateField value={from} onChange={setFrom} placeholder="From" id="nl-from" />
            <span className="muted">to</span>
            <DateField value={to} onChange={setTo} placeholder="To" id="nl-to" />
          </div>
        </div>
        <div className="list-row head" style={{ gridTemplateColumns: cols }}>
          <span className="list-cell">Time</span>
          <span className="list-cell">Type</span>
          <span className="list-cell">Client</span>
          <span className="list-cell">Detail</span>
          <span className="list-cell">Channel</span>
        </div>
        {error ? (
          <div className="list-row" style={{ cursor: 'default' }}>
            <span className="list-cell muted">{error}</span>
          </div>
        ) : rows === null ? (
          <div className="list-row" style={{ cursor: 'default' }}>
            <span className="list-cell muted">Loading…</span>
          </div>
        ) : rows.length === 0 ? (
          <div className="list-row" style={{ cursor: 'default' }}>
            <span className="list-cell muted">
              {type || from || to
                ? 'No notifications match these filters.'
                : 'No notifications yet.'}
            </span>
          </div>
        ) : (
          rows.map((r) => (
            <div
              key={r.id}
              className="list-row"
              style={{ gridTemplateColumns: cols, cursor: 'default' }}
            >
              <span className="list-cell muted">{when(r.ts)}</span>
              <span className="list-cell">{NOTIFICATION_TYPES[r.type] ?? r.type}</span>
              <span className="list-cell">{clientOf(r)}</span>
              <span className="list-cell muted">{r.detail || '—'}</span>
              <span className="list-cell muted">{r.channel || '—'}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
